// 📄 pages/Proyectos/components/ComunicacionUserModal.tsx
// Sub-fase K5: alta/edición de un Communication User del pool (ver
// comunicacionService.ts, K4). Cada ambiente (DEV/QAS/PRD) tiene su propio
// username — en la práctica real de SAP casi siempre difieren.
import React, { useState } from 'react';
import { X, Save, RefreshCw } from 'lucide-react';
import {
  ComunicacionUser, ComunicacionUserData, TipoComunicacionUser, entornosVaciosUser
} from '../../../services/comunicacionService.ts';

interface Props {
  usuario?: ComunicacionUser | null;
  onSave: (data: ComunicacionUserData) => Promise<void>;
  onClose: () => void;
}

const AMBIENTES = ['DEV', 'QAS', 'PRD'] as const;

export default function ComunicacionUserModal({ usuario, onSave, onClose }: Props) {
  const [tipo, setTipo] = useState<TipoComunicacionUser>(usuario?.tipo || 'inbound');
  const [descripcion, setDescripcion] = useState(usuario?.descripcion || '');
  const [entornos, setEntornos] = useState(usuario?.entornos || entornosVaciosUser());
  const [ambienteActivo, setAmbienteActivo] = useState<typeof AMBIENTES[number]>('DEV');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setGuardando(true);
    try {
      await onSave({ tipo, descripcion, entornos });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Error al guardar el Communication User.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-zinc-900 border border-white/10 rounded-2xl p-6 w-full max-w-lg space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white">{usuario ? 'Editar' : 'Nuevo'} Communication User</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Tipo</label>
              <select
                value={tipo}
                onChange={(e) => setTipo(e.target.value as TipoComunicacionUser)}
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
              >
                <option value="inbound">Inbound</option>
                <option value="outbound">Outbound</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Descripción</label>
              <input
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="ej. Usuario inbound para SLP"
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div>
            <div className="flex gap-1.5 mb-2">
              {AMBIENTES.map((amb) => (
                <button
                  key={amb}
                  type="button"
                  onClick={() => setAmbienteActivo(amb)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    ambienteActivo === amb ? 'bg-blue-500/15 text-blue-400 border border-blue-500/30' : 'text-gray-400 hover:text-white border border-transparent'
                  }`}
                >
                  {amb}
                </button>
              ))}
            </div>
            <div className="space-y-2 bg-black/20 border border-white/10 rounded-xl p-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Username en {ambienteActivo}</label>
                <input
                  value={entornos[ambienteActivo].nombre}
                  onChange={(e) => setEntornos({ ...entornos, [ambienteActivo]: { ...entornos[ambienteActivo], nombre: e.target.value } })}
                  placeholder="ej. SLP_INBOUND_USER"
                  className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono"
                  required={ambienteActivo === 'DEV'}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Notas ({ambienteActivo})</label>
                <input
                  value={entornos[ambienteActivo].notas}
                  onChange={(e) => setEntornos({ ...entornos, [ambienteActivo]: { ...entornos[ambienteActivo], notas: e.target.value } })}
                  className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>
          </div>

          {error && <p className="text-red-400 text-xs">{error}</p>}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 py-2 rounded-lg text-sm font-semibold transition-all">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={guardando}
              className="flex-1 flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 py-2 rounded-lg text-sm font-semibold transition-all"
            >
              {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Guardar</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
